import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../models';
import { HEATING_CHARGE } from '../../../../constants';
import type { HTMLElementEvent } from '../../../../models/Types';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function HeatingCharge({ section, setSectionValues }: Props) {
  function onMonthlyHeatingChargeChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [HEATING_CHARGE.FIELD_NAME.MONTHLY_CHARGE]: value,
    });
  }

  function onAnnualHeatingChargeChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [HEATING_CHARGE.FIELD_NAME.ANNUAL_CHARGE]: value,
    });
  }

  return (
    <>
      <Paragraph fontType="h6">Heating</Paragraph>
      <FormField
        marginY="sm"
        label={
          <Label>
            What is the average monthly cost of utility service of the utility providing the primary source of heat
            based on the energy consumption during the previous 12 months of occupancy?
          </Label>
        }
        control={
          <AdornedInput
            leftAdornment={<Adornment>$</Adornment>}
            rightAdornment={<Adornment>per month</Adornment>}
            input={
              <CurrencyInput
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                value={section.getFieldValue(HEATING_CHARGE.FIELD_NAME.MONTHLY_CHARGE)}
                onChange={onMonthlyHeatingChargeChange}
                onBlur={onMonthlyHeatingChargeChange}
              />
            }
          />
        }
      />
      <FormField
        label={
          <Label>
            What is the projected annual cost of utility service of the utility providing the primary source of heat
            based on the energy consumption during the previous 12 months of occupancy?
          </Label>
        }
        control={
          <AdornedInput
            leftAdornment={<Adornment>$</Adornment>}
            input={
              <CurrencyInput
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                value={section.getFieldValue(HEATING_CHARGE.FIELD_NAME.ANNUAL_CHARGE)}
                onChange={onAnnualHeatingChargeChange}
                onBlur={onAnnualHeatingChargeChange}
              />
            }
          />
        }
        description={<FormHelp>If you don’t know the projected annual cost, multiply your monthly cost by 12</FormHelp>}
      />
    </>
  );
}

HeatingCharge.description = 'Chicago specific screen asking about annual heating charge';

export default HeatingCharge;
